<template>
  <main class="home">
    <Header
      :screen-title="screenTitle"
      navbar-type="goBack"
      @clicked="goBack"
    />
    <div class="container mt-5 ps-3">
      <div v-if="periods.length === 0">
        <h2 id="no-statements-title">
          {{ t("NO_ACCOUNT_STATEMENTS") }}
        </h2>
      </div>
      <div
        v-else
        class="statement-item"
        v-for="(period, index) in periods"
        :key="index"
        @click="statementClick(period)"
      >
        <div class="icon-container">
          <img
            :src="`${useAsset('img/credit-line/pdf.svg')}`"
            alt="statement icon"
          />
        </div>
        <p>
          {{
            t("PERIOD_STATEMENT_ITEM", {
              date1: getPeriodsStatementsDates(period.startDate),
              date2: getPeriodsStatementsDates(period.endDate),
            })
          }}
        </p>
      </div>
    </div>
  </main>
</template>

<script lang="ts" setup>
import { ref, onMounted } from "vue";
import Header from "@/shared-components/consumer/Header.vue";
import { Service } from "@/apps/credit-line/services/network.service";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import useAsset from "@/shared-helpers/useAsset";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import type {
  IStatementsPeriodsContainer,
  IStatementsPeriod,
} from "@/apps/credit-line/models/IAccountStatement";
import { getPeriodsStatementsDates } from "@/apps/credit-line/helpers/statements.helper";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import type { AndroidInterface } from "@/apps/credit-line/services/android.service";
import type { RouteLocationRaw } from "vue-router";
import { useRoute, useRouter } from "vue-router";
declare const Android: AndroidInterface;

// Define props: add a boolean prop called isDelinquent
const props = defineProps<{ isDelinquent?: boolean }>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const CreditLineService = new Service();
const screenTitle = ref(t("HISTORICAL_BALANCES_TITLE"));
const periodsContainer = ref({} as IStatementsPeriodsContainer);
const periods = ref<IStatementsPeriod[]>([]);

onMounted(async () => {
  try {
    const response = await CreditLineService.getStatementsPeriods({
      deviceTag: AuthenticationHelper.getDeviceTag(),
    });
    periodsContainer.value = response.data;
    if (periodsContainer.value.periods) {
      periods.value = periodsContainer.value.periods;
    }
  } catch (error) {
    console.log(error);
  }
  if (isAndroid()) {
    Android.finishedLoading();
  }
});

const statementClick = (period: IStatementsPeriod) => {
  let navigationObject: RouteLocationRaw;
  // Prefer the prop when provided, otherwise fall back to route query
  let isDelinquent =
    typeof props.isDelinquent === "boolean"
      ? props.isDelinquent
      : route.query?.isDelinquent === "true";
  const urlParams = new URLSearchParams(window.location.search);
  navigationObject = {
    name: "BalanceDetailsView",
    query: {
      startDate: period.startDate,
      endDate: period.endDate || null,
      startTimestamp: period.startTimestamp,
      endTimestamp: period.endTimestamp || null,
      isDelinquent: isDelinquent ? "true" : "false",
    },
  };

  if (typeof urlParams.get("deviceTag") === "string") {
    let query = navigationObject["query"];
    navigationObject["query"] = {
      ...query,
      deviceTag: urlParams.get("deviceTag"),
    };
  }

  router.push(navigationObject);
};

const goBack = () => {
  if (!isAndroid()) {
    window.close();
  } else {
    if (
      typeof Android?.shouldDisplayNewUi === "function" &&
      Android.shouldDisplayNewUi()
    ) {
      Android.dismissWebView();
    } else {
      router.back();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.statement-item {
  display: flex;
  align-items: center;
  justify-items: center;
  text-decoration: underline;
  &:hover {
    cursor: pointer !important;
  }
}

.statement-item p {
  font-size: medium;
  color: black;
  font-weight: normal;
}

.icon-container {
  display: flex;
  border: none;
  padding: 0;
  margin: 0;
}

.container {
  display: flex;
  flex-direction: column;
}

#no-statements-title {
  background-color: $pj-color-light-silver;
  font-size: medium;
  border-radius: 11px;
  padding: 2rem;
}
</style>
